/**
 * @file index.tsx
 *
 * @fileoverview Renders a single flex item.
 */

import React from "react";
import {
  AlignSelfProps,
  JustifySelfProps,
} from "../../../../../theme/layout/itemsLayout";
import {
  excludeSpacingProps,
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../../../theme/spacing";
import { getScopedClasses } from "../../../../../utils/responsive";

import styles from "./flexItem.module.scss";

type FlexGrowProps = 0 | 1 | 2 | 3 | 4 | 5;

type FlexItemProps = SpaceProps & {
  /**
   * Flex property
   */
  flex?: any;
  /**
   * The content to be displayed inside the chip element.
   *
   * @default "null"
   */
  children?: React.ReactNode | string;
  /**
   * Align self responsive props.
   */
  alignSelf?: AlignSelfProps | AlignSelfProps[];
  /**
   * Justify self responsive props.
   */
  justifySelf?: JustifySelfProps | JustifySelfProps[];
  /**
   * Flex basis (can't be made responsive because accepts string values).
   */
  flexBasis?: string;
  /**
   * Responsive flex grow prop.
   */
  flexGrow?: FlexGrowProps | FlexGrowProps[];
  /**
   * The className.
   * This prop gets appended to the already existing classes.
   */
  className?: string;
};

/**
 * Display multiple items in a flex structure. Useful for laying single dimensionally
 * a set of items together.
 */
export const FlexItem = ({
  flex,
  children,
  alignSelf,
  justifySelf,
  flexBasis,
  flexGrow,
  className = "",
  ...props
}: FlexItemProps) => {
  return (
    <div
      className={`
        flex-item-element
        ${generateMarginClasses(props)}
        ${generatePaddingClasses(props)}
        ${getScopedClasses(styles, "as", alignSelf)}
        ${getScopedClasses(styles, "js", justifySelf)}
        ${getScopedClasses(styles, "fg", flexGrow)}
        ${styles.flexItem}
        ${className}
      `.trim()}
      style={{
        ...(flex ? { flex } : null),
        ...(flexBasis ? { flexBasis } : null),
      }}
      {...excludeSpacingProps(props)}
    >
      {children && children}
    </div>
  );
};

export default FlexItem;
